import { useI18n } from 'vue-i18n'
import { EMAIL_REGEX } from '@/constants'

export type ValidationRulesOptions = {
  messages?: {
    required?: string
    email?: string
    number?: string
  }
}

/**
 * Centralized form validation rules used across the app.
 * Messages are localized via vue-i18n (`validation.*` keys). Must be called
 * from a component setup context (like every existing caller).
 */
export function useValidationRules(options?: ValidationRulesOptions) {
  const { t } = useI18n()

  const requiredMsg = options?.messages?.required ?? t('validation.required')
  const emailMsg = options?.messages?.email ?? t('validation.emailFormat')
  const numberMsg = options?.messages?.number ?? t('validation.number')

  return {
    // Existing rules
    required: (value: string) => !!value || requiredMsg,
    email: (value: string) => !!String(value).match(EMAIL_REGEX) || emailMsg,
    number: (value: string) => /^\d+$/.test(String(value)) || numberMsg,

    // Length rules
    minLength: (min: number) => (value: string) =>
      !value || value.length >= min || t('validation.minLength', { min }),
    maxLength: (max: number) => (value: string) =>
      !value || value.length <= max || t('validation.maxLength', { max }),

    // Format rules
    phone: (value: string) =>
      !value || /^\d+$/.test(value) || t('validation.phone'),
    postcode: (value: string) => !value || /^[a-zA-Z0-9\s-]{3,10}$/.test(value) || t('validation.postcode'),
    alphanumeric: (value: string) =>
      !value ||
      /^[a-zA-Z0-9äöüÄÖÜß\s\-.,]+$/.test(value) ||
      t('validation.alphanumeric'),

    // Enhanced email validation with progressive error messages
    emailDetailed: (value: string): string | true => {
      if (!value) return t('validation.required')
      if (!value.includes('@')) return t('validation.emailMissingAt')
      const parts = value.split('@')
      if (parts.length !== 2 || !parts[1]) return t('validation.emailMissingDomain')
      if (!parts[1].includes('.')) return t('validation.emailInvalidDomain')
      if (!EMAIL_REGEX.test(value)) return t('validation.email')
      return true
    },

    // Enhanced password validation with granular feedback
    passwordMinLength: (min: number) => (value: string): string | true => {
      if (!value) return t('validation.required')
      if (value.length < min) return t('validation.passwordTooShort', { min, current: value.length })
      return true
    },
    passwordUppercase: (value: string): string | true => {
      if (!value) return true // Let required rule handle empty
      if (!/[A-Z]/.test(value)) return t('validation.passwordNeedsUppercase')
      return true
    },
    passwordLowercase: (value: string): string | true => {
      if (!value) return true
      if (!/[a-z]/.test(value)) return t('validation.passwordNeedsLowercase')
      return true
    },
    passwordNumber: (value: string): string | true => {
      if (!value) return true
      if (!/\d/.test(value)) return t('validation.passwordNeedsNumber')
      return true
    },
    // Combined password complexity check
    passwordComplex: (value: string): string | true => {
      if (!value) return t('validation.required')
      if (value.length < 8) return t('validation.passwordTooShort', { min: 8, current: value.length })
      if (!/[A-Z]/.test(value)) return t('validation.passwordNeedsUppercase')
      if (!/[a-z]/.test(value)) return t('validation.passwordNeedsLowercase')
      if (!/\d/.test(value)) return t('validation.passwordNeedsNumber')
      return true
    },

    // Enhanced phone validation with specific messages
    phoneDetailed: (value: string): string | true => {
      if (!value) return t('validation.required')
      if (/[a-zA-Z]/.test(value)) return t('validation.phoneNoLetters')
      if (/\s/.test(value)) return t('validation.phoneNoSpaces')
      const digitsOnly = value.replace(/\D/g, '')
      if (digitsOnly.length < 6) return t('validation.phoneTooShort', { min: 6 })
      if (digitsOnly.length > 15) return t('validation.phoneTooLong', { max: 15 })
      return true
    },

    // Enhanced postcode validation with specific messages
    postcodeDetailed: (value: string): string | true => {
      if (!value) return t('validation.required')
      if (value.length < 3) return t('validation.postcodeTooShort', { current: value.length })
      if (value.length > 10) return t('validation.postcode')
      if (!/^[a-zA-Z0-9\s-]+$/.test(value)) return t('validation.postcodeNoLetters')
      return true
    },

    // Name validation (no numbers or special chars)
    name: (value: string): string | true => {
      if (!value) return t('validation.required')
      if (/\d/.test(value)) return t('validation.nameNoNumbers')
      if (!/^[a-zA-ZäöüÄÖÜß\s\-.']+$/.test(value)) return t('validation.nameInvalidChars')
      return true
    },

    // Security rules - enhanced to catch more attack vectors
    noHtml: (value: string) => {
      if (!value) return true
      // Check for HTML tags, comments, CDATA, and null bytes
      const htmlPatterns = [
        /<[^>]*>/g, // HTML tags
        /<!--[\s\S]*?-->/g, // HTML comments
        /<!\[CDATA\[/gi, // CDATA sections
        /\0/g // Null bytes
      ]
      return !htmlPatterns.some((pattern) => pattern.test(value)) || t('validation.noHtml')
    },
    noScript: (value: string) => {
      if (!value) return true
      // Check for dangerous protocols and event handlers
      const scriptPatterns = [
        /javascript:/gi,
        /vbscript:/gi,
        /data:/gi,
        /on\w+\s*=/gi, // Event handlers (onclick=, onload=, etc.)
        /<script/gi,
        /<\/script/gi,
        /expression\s*\(/gi, // CSS expression()
        /url\s*\(/gi // CSS url() can be dangerous
      ]
      return !scriptPatterns.some((pattern) => pattern.test(value)) || t('validation.invalidInput')
    }
  }
}
